import { registerIndicator, type Indicator, type KLineData } from 'klinecharts'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { sessionDayKey, DEFAULT_SESSION_TZ } from './sessionDay'
import { REGULAR_OPEN_MINUTES, sessionClock } from './sessionFilter'

/** Where each day's VWAP starts accumulating (the "Session start" input). */
export type VwapAnchor = 'premarket' | 'regular'

export interface VwapOptions {
  /** 'premarket' (default): from the day's first bar. 'regular': from 09:30
   *  in the session timezone; earlier bars carry no value. */
  anchor?: VwapAnchor
  /** Mark each session's last bar so the line can break there. */
  sessionBreaks?: boolean
}

export interface VwapRow {
  vwap: number | undefined
  /** Last bar before a new session, only when `sessionBreaks` is on. */
  sessionEnd?: true
}

/**
 * Session VWAP: cumulative volume-weighted average price that resets at the
 * start of each EXCHANGE-SESSION day. The session day is bucketed in the
 * instrument's session timezone (ET for US equities) — NOT UTC — because a US
 * session straddles UTC midnight (after-hours ~20:00 ET = ~00:00 UTC next day),
 * so a UTC reset would wrongly restart VWAP mid-session. Pure + exported so the
 * reset logic is unit-testable (mirrors `computePivotPoints`).
 */
export function computeVwap(
  dataList: readonly KLineData[],
  tz: string = DEFAULT_SESSION_TZ,
  { anchor = 'premarket', sessionBreaks = false }: VwapOptions = {},
): VwapRow[] {
  let cumTPV = 0
  let cumVol = 0
  let prevKey: string | null = null
  const keys = dataList.map((bar) => sessionDayKey(bar.timestamp, tz))

  return dataList.map((bar, i) => {
    const key = keys[i]
    if (key !== prevKey) {
      cumTPV = 0
      cumVol = 0
      prevKey = key
    }

    const row: VwapRow = { vwap: undefined }
    if (sessionBreaks && i + 1 < keys.length && keys[i + 1] !== key) row.sessionEnd = true
    // Regular-open anchor: premarket bars neither show nor accumulate.
    if (anchor === 'regular' && sessionClock(bar.timestamp, tz).minutes < REGULAR_OPEN_MINUTES) {
      return row
    }

    const tp = (bar.high + bar.low + bar.close) / 3
    const vol = bar.volume ?? 0
    cumTPV += tp * vol
    cumVol += vol
    row.vwap = cumVol > 0 ? cumTPV / cumVol : tp
    return row
  })
}

/** An invisible stroke: klinecharts has no per-segment "skip", only styles. */
const NO_STROKE = { color: 'rgba(0,0,0,0)' }

/**
 * VWAP (Volume Weighted Average Price) indicator, rendered as a line on the
 * main (price) pane. Session timezone rides in via the indicator's `extendData`
 * (same pattern as PIVOT / session indicators), defaulting to ET.
 */
export const registerVwapIndicator = registerOnce(() => {
  registerIndicator({
    name: 'VWAP',
    shortName: 'VWAP',
    series: 'price' as never,
    calcParams: [],
    precision: 2,
    figures: [
      {
        key: 'vwap',
        title: 'VWAP: ',
        type: 'line',
        // Line-break input. klinecharts strokes one segment per bar, from that
        // bar to the next, styled by the bar it starts from, so hiding the
        // segment that leaves a session's last bar starts each day fresh.
        styles: ({ current }: { current: { indicatorData?: VwapRow } }) =>
          current.indicatorData?.sessionEnd ? NO_STROKE : {},
      },
    ],
    calc: (dataList: KLineData[], indicator: Indicator) => {
      const tz = (indicator.extendData as { timezone?: string } | undefined)?.timezone
      // Inputs: [session start, line breaks]. Missing slots keep the defaults.
      const [anchor, sessionBreaks] = indicator.calcParams ?? []
      return computeVwap(dataList, tz || DEFAULT_SESSION_TZ, {
        anchor: anchor === 'regular' ? 'regular' : 'premarket',
        sessionBreaks: sessionBreaks === true,
      })
    },
  } as IndicatorTemplateParam)
})
